import { escapeHtml } from '../util/escapeHtml.js';
import { mountShell, STATUS_GLYPH } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { navigate } from '../router.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { getTitleDef, customTitleCss, customChipCss, titleChipHtml } from '../components/Title.js';
import { newArrivalPill } from '../util/newArrivalBadge.js';
import { openAttackBanner } from '../components/AttackBanner.js';
import type { PublicProfileDto } from '@rex-mortis/shared';

/**
 * Read-only "scouting card" for another player. Reached via /u/:id — names are
 * clickable across the Annals, Attacks, Replays, Houses, and Shoutbox. Shows
 * only the public subset (no crowns/bank/inventory/email), plus an action bar
 * (Attack, Mail, Acquaintance, Enemy, Send Coin, Break Out / Revive) when
 * viewing someone other than yourself.
 */
export async function renderPublicProfile(root: HTMLElement, params?: Record<string, string>): Promise<void> {
  const id = params?.id ?? '';

  let mainEl: HTMLElement | null = null;
  mountShell(root, (main) => {
    mainEl = main;
    main.innerHTML = `<p style="color: var(--c-ink-soft);">Consulting the heralds…</p>`;
  });
  if (!mainEl) return; // No viewer character — shell will have redirected.
  const main = mainEl as HTMLElement;

  if (!id) {
    main.innerHTML = errorHtml('No character was named.');
    return;
  }

  try {
    const { profile } = await api.publicProfile(id);
    const isSelf = store.get().character?.id === profile.id;
    main.innerHTML = renderCard(profile, isSelf);
    if (!isSelf) wireActions(main, profile);
  } catch (err) {
    const notFound = err instanceof HttpError && err.status === 404;
    main.innerHTML = errorHtml(
      notFound ? 'No such soul walks these lands.' : 'The heralds could not produce that record.',
    );
  }
}

// ── Action wiring ───────────────────────────────────────────────────────────

function wireActions(main: HTMLElement, p: PublicProfileDto): void {
  const flash = (kind: 'success' | 'danger', msg: string): void => {
    const box = main.querySelector<HTMLDivElement>('#ppf-flash');
    if (!box) return;
    box.innerHTML = `<div class="alert ${kind}">${escapeHtml(msg)}</div>`;
  };
  const errMsg = (err: unknown): string => (err instanceof HttpError ? err.message : 'Something went wrong.');

  main.querySelector<HTMLButtonElement>('[data-act="attack"]')?.addEventListener('click', () => {
    // Strike straight from the scouting card via the floating pop-out — no
    // detour through the Attacks page. On a completed strike, reload the card so
    // the target's status (e.g. now in the Infirmary) refreshes.
    openAttackBanner(
      { id: p.id, name: p.name, level: p.level, location: p.location, status: p.status },
      { onResult: () => navigate(`/u/${encodeURIComponent(p.id)}`) },
    );
  });
  main.querySelector<HTMLButtonElement>('[data-act="mail"]')?.addEventListener('click', () => {
    navigate(`/mail?to=${encodeURIComponent(p.name)}`);
  });

  main.querySelector<HTMLButtonElement>('[data-act="friend"]')?.addEventListener('click', async () => {
    try {
      await api.addFriend({ handle: p.name });
      flash('success', `${p.name} added to your Acquaintances.`);
    } catch (err) {
      flash('danger', errMsg(err));
    }
  });

  // Mark Enemy — a personal rival/target marker. No mechanical effect; you can
  // still mail, attack, and trade with them. (Distinct from Block.)
  main.querySelector<HTMLButtonElement>('[data-act="enemy"]')?.addEventListener('click', async () => {
    try {
      await api.addEnemy({ handle: p.name });
      flash('success', `${p.name} added to your Enemies — marked as a target.`);
    } catch (err) {
      flash('danger', errMsg(err));
    }
  });

  // Block — stops mail + crown transfers both ways. Does NOT stop attacks, but
  // repeated harassment from a blocked attacker triggers a long cooldown.
  main.querySelector<HTMLButtonElement>('[data-act="block"]')?.addEventListener('click', async () => {
    const ok = await showConfirmDialog({
      title: 'Block this player?',
      message: `Block ${p.name}? Neither of you can send mail or crowns to the other. They can still attack you — but if they hit you 10 times in 6 hours, they're locked out for 72 hours.`,
      confirmLabel: 'Block',
    });
    if (!ok) return;
    try {
      await api.addBlock({ handle: p.name });
      flash('success', `${p.name} blocked.`);
    } catch (err) {
      flash('danger', errMsg(err));
    }
  });

  main.querySelector<HTMLButtonElement>('[data-act="breakout"]')?.addEventListener('click', async () => {
    const ok = await showConfirmDialog({
      title: 'Break them out?',
      message: `Spend Resolve to break ${p.name} out of the Dungeon?`,
      confirmLabel: 'Break Out',
    });
    if (!ok) return;
    try {
      const r = await api.breakoutPrisoner(p.id);
      store.set({ character: r.character });
      flash('success', `You freed ${r.freedName} from the Dungeon.`);
    } catch (err) {
      flash('danger', errMsg(err));
    }
  });

  main.querySelector<HTMLButtonElement>('[data-act="revive"]')?.addEventListener('click', async () => {
    const ok = await showConfirmDialog({
      title: 'Revive them?',
      message: `Pour your own Vigor and Life into reviving ${p.name} from the Infirmary?`,
      confirmLabel: 'Revive',
    });
    if (!ok) return;
    try {
      const r = await api.revivePatient(p.id);
      store.set({ character: r.character });
      flash('success', `You revived ${r.revivedName}.`);
    } catch (err) {
      flash('danger', errMsg(err));
    }
  });

  // Send Coin — reveal an inline amount field, then confirm + send.
  const coinBox = main.querySelector<HTMLDivElement>('#ppf-coin');
  main.querySelector<HTMLButtonElement>('[data-act="coin"]')?.addEventListener('click', () => {
    if (coinBox) {
      coinBox.hidden = !coinBox.hidden;
      if (!coinBox.hidden) main.querySelector<HTMLInputElement>('#ppf-coin-amount')?.focus();
    }
  });
  main.querySelector<HTMLButtonElement>('#ppf-coin-cancel')?.addEventListener('click', () => {
    if (coinBox) coinBox.hidden = true;
  });
  main.querySelector<HTMLButtonElement>('#ppf-coin-send')?.addEventListener('click', async () => {
    const input = main.querySelector<HTMLInputElement>('#ppf-coin-amount');
    const raw = (input?.value ?? '').trim();
    if (!/^\d+$/.test(raw) || raw === '0') {
      flash('danger', 'Enter a whole number of crowns greater than zero.');
      return;
    }
    const ok = await showConfirmDialog({
      title: 'Send crowns?',
      message: `Send ${Number(raw).toLocaleString()} crowns to ${p.name}? This cannot be undone.`,
      confirmLabel: 'Send Coin',
      tone: 'gold',
    });
    if (!ok) return;
    try {
      const r = await api.sendCrowns({ toHandle: p.name, amount: raw });
      store.set({ character: r.character });
      if (coinBox) coinBox.hidden = true;
      flash(
        'success',
        `Sent ${Number(r.amountSent).toLocaleString()} crowns to ${r.recipientName}. You may send ${Number(r.dailyRemaining).toLocaleString()} more today.`,
      );
    } catch (err) {
      flash('danger', errMsg(err));
    }
  });
}

// ── Rendering ─────────────────────────────────────────────────────────────

function renderCard(p: PublicProfileDto, isSelf: boolean): string {
  const rankDef = getTitleDef(p.levelRankSlug);
  const titleDef = getTitleDef(p.activeTitleSlug);
  const rankText = rankDef?.text ?? '—';
  const avatarSrc = p.activeAvatarUrl ?? '/img/avatar-default.webp';

  const statusLabel = p.status.charAt(0) + p.status.slice(1).toLowerCase();
  const total = p.strength + p.defense + p.speed + p.dexterity;

  // Active Shade-wound — public weakness. Pill + per-stat effective values.
  const woundPct = p.wound?.pct ?? 0;
  const woundHealsIn = (() => {
    if (!p.wound) return '';
    const ms = Date.parse(p.wound.healsAt) - Date.now();
    if (ms <= 0) return 'any moment';
    const totalM = Math.ceil(ms / 60000);
    const h = Math.floor(totalM / 60);
    return h > 0 ? `${h}h ${totalM % 60}m` : `${totalM}m`;
  })();
  const woundPill = p.wound
    ? `<span class="pill" style="background:rgba(122,28,28,0.16); border-color:var(--c-oxblood); color:var(--c-oxblood);" title="Wounded by the Shade — every battle stat is cut by ${woundPct}% in combat until it heals.">🩸 Shade-wound −${woundPct}% · heals in ${woundHealsIn}</span>`
    : '';
  const statCell = (v: number): string =>
    woundPct > 0
      ? `${fmt(v)} <span style="color:var(--c-oxblood); font-weight:700;" title="Effective in combat while wounded">→ ${fmt(Math.round(v * (1 - woundPct / 100)))}</span>`
      : fmt(v);

  // Membership flair — public name colour + tier pill, mirroring the owner's
  // own Profile. Only renders for an active member (server sends null otherwise).
  const flair = p.membershipFlairColour ?? null;
  const nameInner = flair
    ? `<span style="color:${flair}; text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000,0 0 10px ${flair}88;">${escapeHtml(p.name)}</span>`
    : escapeHtml(p.name);
  const memberPill =
    p.membershipTier && flair
      ? `<span class="pill" style="border-color:${flair}; background:${flair}1f; color:${flair}; font-weight:700; text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;">${escapeHtml(p.membershipTier.charAt(0).toUpperCase() + p.membershipTier.slice(1))}</span>`
      : '';

  // "New Arrival" badge — shown only while the player is inside their PvP
  // protection window. The server sends protectionEndsAt (null once lapsed), so
  // this auto-disappears with no client-side window hardcoded.
  const newcomerPill = newArrivalPill(p.protectionEndsAt);

  // Bestowed titles carry bespoke colour overrides (Kaan's blue outline, the
  // founder gold→crimson gradient) so they read the same on others' views.
  // Marble-pill titles render as their full chip so the pill backs the text
  // (light-fill text would be illegible on parchment). Others use bare colour.
  const titleIsChip = !!customChipCss(titleDef?.slug);
  const titleCustomCss = customTitleCss(titleDef?.slug);
  const titleValueStyle = !titleIsChip && titleCustomCss ? ` style="${titleCustomCss}"` : '';
  const titleValueInner = titleDef
    ? (titleIsChip ? titleChipHtml(titleDef.slug) : escapeHtml(titleDef.text))
    : '';
  const titleRow = titleDef
    ? `<div class="ppf-row"><span class="ppf-label">TITLE</span><span class="ppf-value"${titleValueStyle}>${titleValueInner}</span></div>`
    : '';

  const houseHtml = p.house
    ? `<h2 class="ppf-heading">House</h2>
       <div class="ppf-box">
         <strong>${escapeHtml(p.house.name)}</strong> <span class="pill">[${escapeHtml(p.house.tag)}]</span>
         <span class="pill">${escapeHtml(rankLabel(p.house.rank))}</span>
       </div>`
    : '';

  const pinned = new Set(p.pinnedAwardSlugs);
  const awardsHtml =
    p.awards.length > 0
      ? `<h2 class="ppf-heading">Awards <span class="ppf-count">(${p.awards.length})</span></h2>
         <div class="ppf-awards">
           ${p.awards
             .slice()
             .sort((a, b) => (pinned.has(b.slug) ? 1 : 0) - (pinned.has(a.slug) ? 1 : 0))
             .map(
               (a) =>
                 `<span class="pill ${pinned.has(a.slug) ? 'success' : ''}" title="${escapeHtml(a.name)}">${
                   pinned.has(a.slug) ? '★ ' : ''
                 }${escapeHtml(a.name)}</span>`,
             )
             .join('')}
         </div>`
      : `<h2 class="ppf-heading">Awards</h2><p class="ppf-muted">No deeds yet recorded.</p>`;

  const since = p.joinedAt
    ? new Date(p.joinedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  // Human "last active" — relative for recent activity, falling back to a date
  // once it's older than a month.
  const relativeFromNow = (iso: string): string => {
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const lastActive = p.lastActiveAt ? relativeFromNow(p.lastActiveAt) : null;

  // Action bar — only for OTHER players. Break Out / Revive appear only when the
  // target is in the matching status.
  const actionsHtml = isSelf
    ? ''
    : `
      <div id="ppf-flash"></div>
      <div class="ppf-actions">
        <button class="btn" data-act="attack">⚔ Attack</button>
        <button class="btn ghost" data-act="mail">✉ Send Mail</button>
        <button class="btn ghost" data-act="friend">+ Acquaintance</button>
        <button class="btn ghost" data-act="enemy">⚑ Mark Enemy</button>
        <button class="btn ghost" data-act="block">⊘ Block</button>
        <button class="btn ghost" data-act="coin">⛀ Send Coin</button>
        ${p.status === 'DUNGEON' ? `<button class="btn" data-act="breakout">⛓ Break Out</button>` : ''}
        ${p.status === 'INFIRMARY' ? `<button class="btn" data-act="revive">✚ Revive</button>` : ''}
      </div>
      <div id="ppf-coin" class="ppf-coin" hidden>
        <input id="ppf-coin-amount" type="number" min="1" inputmode="numeric" placeholder="Crowns to send" />
        <button class="btn gold" id="ppf-coin-send">Send</button>
        <button class="btn ghost" id="ppf-coin-cancel">Cancel</button>
      </div>`;

  return `
    <div class="ppf-back"><a href="#/leaderboards">← The Annals</a></div>
    <div class="card ppf-card">
      <div class="ppf-identity">
        <span class="ppf-avatar"><img src="${escapeHtml(avatarSrc)}" alt="${escapeHtml(p.name)}" /></span>
        <div class="ppf-identity-text">
          <div class="ppf-name">${nameInner}</div>
          <hr class="ppf-rule" />
          <div class="ppf-row"><span class="ppf-label">RANK</span><span class="ppf-value">${escapeHtml(rankText)}</span></div>
          ${titleRow}
        </div>
      </div>

      <div class="ppf-pills">
        ${newcomerPill}
        ${memberPill}
        <span class="pill">Level <strong>${p.level}</strong></span>
        ${p.soulNumber ? `<span class="pill" title="Soul #${p.soulNumber} to join the realm.">Soul #${p.soulNumber}</span>` : ''}
        <span class="pill">Status: <span style="font-size:1.1em; margin-right:4px;">${STATUS_GLYPH[p.status] ?? ''}</span><strong>${escapeHtml(statusLabel)}</strong></span>
        <span class="pill">Location: <strong>${escapeHtml(fmtLocation(p.location))}</strong></span>
        ${woundPill}
      </div>

      ${actionsHtml}

      <div class="ppf-cols">
        <div>
          <h2 class="ppf-heading">Battle Stats${woundPct > 0 ? ` <span style="font-size:0.75rem; color:var(--c-oxblood); font-weight:700;">🩸 −${woundPct}% wounded</span>` : ''}</h2>
          <table class="prof-stats ppf-stats">
            <tr><th>Strength</th><td>${statCell(p.strength)}</td></tr>
            <tr><th>Defense</th><td>${statCell(p.defense)}</td></tr>
            <tr><th>Speed</th><td>${statCell(p.speed)}</td></tr>
            <tr><th>Dexterity</th><td>${statCell(p.dexterity)}</td></tr>
            <tr class="total"><th>Total</th><td>${statCell(total)}</td></tr>
          </table>
        </div>
        <div>
          <h2 class="ppf-heading">Reputation</h2>
          <table class="prof-stats ppf-stats">
            <tr><th>Infamy</th><td>${fmt(p.infamy)}</td></tr>
            <tr><th>Cunning</th><td>${fmt(p.cunning)}</td></tr>
            <tr><th>Virtue</th><td>${fmt(p.virtue)}</td></tr>
            <tr><th>Honour</th><td>${fmt(p.honour)}</td></tr>
          </table>
        </div>
      </div>

      ${houseHtml}
      ${awardsHtml}
      ${since ? `<p class="ppf-muted ppf-since">Walks the realm since ${escapeHtml(since)}.</p>` : ''}
      ${lastActive ? `<p class="ppf-muted ppf-since" style="margin-top:-4px;">Last active ${escapeHtml(lastActive)}.</p>` : ''}
    </div>

    <style>
      .ppf-back { margin-bottom: var(--s-3); }
      .ppf-card { padding: var(--s-5) var(--s-6) var(--s-6); }
      .ppf-identity { display: flex; gap: var(--s-4); align-items: center; }
      .ppf-avatar img {
        width: 92px; height: 92px; object-fit: cover; border-radius: var(--radius);
        border: 2px solid var(--c-rule-strong); background: var(--c-parchment);
      }
      .ppf-identity-text { min-width: 0; }
      .ppf-name { font-family: var(--font-display); font-size: 1.8rem; color: var(--c-oxblood); line-height: 1.1; }
      .ppf-rule { border: none; border-top: 1px solid var(--c-rule); margin: var(--s-2) 0; }
      .ppf-row { display: flex; gap: var(--s-3); align-items: baseline; }
      .ppf-label { font-size: 0.72rem; letter-spacing: 0.08em; color: var(--c-ink-soft); width: 48px; }
      .ppf-value { font-weight: 600; }
      .ppf-value .title-chip { margin-left: 0; }
      .ppf-pills { display: flex; flex-wrap: wrap; gap: var(--s-2); margin: var(--s-4) 0; }
      .ppf-actions { display: flex; flex-wrap: wrap; gap: var(--s-2); margin: var(--s-3) 0; }
      .ppf-actions .btn { padding: 8px 14px; font-size: 0.9rem; }
      .ppf-coin {
        display: flex; flex-wrap: wrap; gap: var(--s-2); align-items: center;
        margin: 0 0 var(--s-4); padding: var(--s-3);
        background: var(--c-parchment); border: 1px solid var(--c-rule); border-radius: var(--radius);
      }
      /* The HTML [hidden] attr is otherwise overridden by display:flex above. */
      .ppf-coin[hidden] { display: none; }
      .ppf-coin input {
        font: inherit; padding: var(--s-2) var(--s-3); min-height: 44px;
        /* Colours come from the global pinned cream cell in styles/pages.css. */
        border: 1px solid var(--c-rule); border-radius: var(--radius);
        flex: 1; min-width: 160px;
      }
      .ppf-cols { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s-5); }
      @media (max-width: 540px) { .ppf-cols { grid-template-columns: 1fr; } .ppf-identity { gap: var(--s-3); } }
      .ppf-heading {
        font-family: var(--font-display); color: var(--c-oxblood);
        border-bottom: 1px solid var(--c-rule); padding-bottom: var(--s-1); margin: var(--s-4) 0 var(--s-2);
      }
      .ppf-count { color: var(--c-ink-soft); font-size: 0.85rem; }
      table.prof-stats.ppf-stats { width: 100%; border-collapse: collapse; }
      table.ppf-stats th, table.ppf-stats td { padding: 4px 6px; border-bottom: 1px solid var(--c-rule); }
      table.ppf-stats th { color: var(--c-ink-soft); text-align: left; font-weight: 500; }
      table.ppf-stats td { font-weight: 600; text-align: right; font-family: var(--font-numeric); }
      table.ppf-stats tr.total th { color: var(--c-oxblood); font-family: var(--font-display); }
      table.ppf-stats tr.total td { color: var(--c-oxblood); }
      .ppf-box { display: flex; flex-wrap: wrap; gap: var(--s-2); align-items: center; }
      .ppf-awards { display: flex; flex-wrap: wrap; gap: var(--s-2); }
      .ppf-muted { color: var(--c-ink-soft); }
      .ppf-since { margin-top: var(--s-4); font-style: italic; font-size: 0.9rem; }
    </style>
  `;
}

function errorHtml(message: string): string {
  return `
    <div class="ppf-back"><a href="#/leaderboards">← The Annals</a></div>
    <div class="card" style="padding: var(--s-6); text-align: center;">
      <p style="color: var(--c-ink-soft); font-style: italic;">${escapeHtml(message)}</p>
    </div>
    <style>.ppf-back { margin-bottom: var(--s-3); }</style>
  `;
}

function rankLabel(rank: string): string {
  return rank.charAt(0) + rank.slice(1).toLowerCase();
}

function fmtLocation(loc: string): string {
  return loc
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function fmt(n: number): string {
  return n.toLocaleString();
}

